import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ChartControlBlock } from '../../menu-group/menu-group.component';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  FeatureStatus,
  PopulationService,
} from '../../population-menu/population.service';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { FeatureTooltipComponent } from '../feature-tooltip/feature-tooltip.component';
import { ChartYearSelectionComponent } from '../chart-year-selection/chart-year-selection.component';
import { PlotlyChartComponent } from '../plotly-chart/plotly-chart.component';
import { ChartWarningComponent } from '../chart-warning/chart-warning.component';
import { CustomSlideToggleComponent } from '../../../../../../shared/components/custom-slide-toggle/custom-slide-toggle.component';
import { ModalService } from '../../../../../../shared/services/modal.service';
import { MapModalData } from '../../../../../../user/map-redirect-modal/map-redirect-modal.component';
import { CommuteAndMobilityService } from '../../commute-and-mobility/commute-and-mobility.service';
import { HealthService } from '../../health/health.service';
import { UserAccessService } from '../../../../../../user/access/user-access.service';
import {
  LockCopy,
  lockCopyFor,
} from '../feature-tooltip/inacessible-features-util';
import { LockIconComponent } from '../../../../../../shared/components/lock-icon/lock-icon.component';

@Component({
  selector: 'app-chart-block',
  templateUrl: './chart-block.component.html',
  imports: [
    ReactiveFormsModule,
    NgClass,
    NgTemplateOutlet,
    FeatureTooltipComponent,
    ChartYearSelectionComponent,
    PlotlyChartComponent,
    ChartWarningComponent,
    CustomSlideToggleComponent,
    LockIconComponent,
  ],
  styleUrls: [
    './chart-block.component.scss',
    '../../../../../../app.component.scss',
  ],
  host: {
    '(click)': 'onRowClick()',
    '[class.asks-on-click]': 'lock && disableToggle',
  },
})
export class ChartBlockComponent implements OnInit, OnChanges {
  @Output() changeSelectedYear = new EventEmitter<{
    year: number;
    usedFor: string;
  }>();

  @Input() chart!: ChartControlBlock;
  @Input() toggle!: FormControl;
  @Input() featureStatus!: FeatureStatus;
  /** The visitor's plan does not cover this chart. The switch still works. */
  @Input() locked = false;
  /**
   * The one case where it does not: in the POI group this switch is also the
   * map's own POI layer, so opening a locked one would paint the paid places
   * on the map, not just draw a chart that can be blurred.
   */
  @Input() disableToggle = false;

  /** Asked to unlock, from the gate line or the blurred chart under it. */
  @Output() unlockRequested = new EventEmitter<void>();

  /**
   * Which gate this chart sits behind, or null when the visitor may see it.
   * A locked chart is rendered open on purpose - the panel is the tease.
   */
  public lock: LockCopy | null = null;

  public readonly defaultChartStyle = { height: '430px', 'margin-top': '0px' };

  /**
   * The placeholder stands in for this chart, so it is this chart's height -
   * otherwise every group jumps as its charts arrive.
   */
  public get skeletonHeight(): string {
    return (this.chart.chartStyle?.['height'] ??
      this.defaultChartStyle.height) as string;
  }

  public wasYearSelected = true;

  constructor(
    private accessService: UserAccessService,
    private modalService: ModalService,
    private commuteAndMobilityService: CommuteAndMobilityService,
    private populationService: PopulationService,
    private healthService: HealthService
  ) {}

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.disableToggle && this.toggle) {
      if (this.disableToggle) {
        this.toggle.disable({ emitEvent: false });
      } else {
        this.toggle.enable({ emitEvent: false });
      }
    }

    if (changes.locked || changes.chart) {
      this.lock = this.locked
        ? lockCopyFor(this.chart.featureConst, this.accessService)
        : null;
    }
  }

  /**
   * Which prompt this opens - sign-up, plans or the subscription upsell -
   * depends on the visitor, so the menu group answers it.
   */
  public requestUnlock(): void {
    this.unlockRequested.emit();
  }

  /**
   * A locked row whose switch is disabled has nothing else to click: the chart
   * that normally carries the gate can never be opened. That is the one case
   * where the whole row asks, the way it did before the gate existed - anywhere
   * else the same click is how someone opens the chart, so the row stays inert.
   */
  public onRowClick(): void {
    if (this.lock && this.disableToggle) {
      this.requestUnlock();
    }
  }

  public onSelectedYearChange(data: { year: number; usedFor: string }): void {
    const handlers: { [key: string]: (year: number) => void } = {
      householdsByVehiclesYear: (year: number) =>
        this.commuteAndMobilityService.updateHouseholdsByVehiclesChartByYear(
          year
        ),
      maritalStatusYear: (year: number) =>
        this.populationService.updateMaritalStatusChartByYear(year),
      workCommuteModesYear: (year: number) =>
        this.commuteAndMobilityService.updateWorkCommuteModesChartByYear(year),
      travelTimeToWorkYear: (year: number) =>
        this.commuteAndMobilityService.updateTravelTimeToWorkChartByYear(year),
      timeOfDepartureToWorkYear: (year: number) =>
        this.commuteAndMobilityService.updateTimeOfDepartureToWorkChartByYear(
          year
        ),
      insuranceDistributionYear: (year: number) =>
        this.healthService.updateInsuranceDistributionByYear(year),
    };

    const handler = handlers[data.usedFor];

    if (handler) {
      handler(data.year);
    } else {
      this.changeSelectedYear.emit(data);
    }
  }

  public handleTooltipModalOpen(modalData: MapModalData): void {
    this.modalService.openModal(modalData);
  }
}
